// assets/js/charts.js
import Chart from 'chart.js/auto';

let charts = {
  _instances: {},
  _tooltipEl: null,
  _tooltipCssInjected: false,
  _resizeObserver: null,

  init: function () {
    charts.someFunction();
    charts._bindHoverTooltips(document);

    window.addEventListener('resize', charts._handleResize, { passive: true });
    charts._ensureResizeObserver();
  },

  someFunction() {
    charts._renderBars();
    charts._renderDots();
  },

  // ---------- Shared utils ----------
  _getJson(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      const txt = (el.textContent || '').trim();
      return txt ? JSON.parse(txt) : null;
    } catch (e) {
      console.warn(`Invalid JSON in #${id}`, e);
      return null;
    }
  },

  _ensureCanvas(containerId, canvasId) {
    const container = document.getElementById(containerId);
    if (!container) return null;

    let canvas = container.querySelector('canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = canvasId;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      container.appendChild(canvas);
    }

    return canvas.getContext('2d');
  },

  _destroyIfExists(key) {
    if (charts._instances[key]) {
      charts._instances[key].destroy();
      delete charts._instances[key];
    }
  },

  _handleResize() {
    Object.values(charts._instances).forEach((inst) => {
      if (inst) inst.resize();
    });
  },

  _ensureResizeObserver() {
    if (charts._resizeObserver) return;

    const targets = ['chart-bars', 'chart-dots']
      .map(id => document.getElementById(id))
      .filter(Boolean);

    if (targets.length === 0) return;

    charts._resizeObserver = new ResizeObserver(() => {
      charts._handleResize();
    });

    targets.forEach(el => charts._resizeObserver.observe(el));
  },

  // ---------- Chart.js-style tooltip for ANY element with [title] ----------
  _ensureTooltipEl() {
    if (charts._tooltipEl) return charts._tooltipEl;

    if (!charts._tooltipCssInjected) {
      const css = `
        .cj-tooltip {
          position: fixed;
          z-index: 999999;
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -100%);
          transition: opacity 80ms linear;
          background: rgba(0,0,0,0.9);
          border: 1px solid rgba(181,213,55,0.65);
          color: #fff;
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.2;
          box-shadow: 0 6px 18px rgba(0,0,0,0.35);
          max-width: min(60vw, 420px);
          word-wrap: break-word;
          white-space: nowrap;
        }
        @media (max-width: 640px) {
          .cj-tooltip { white-space: normal; }
        }
      `;
      const style = document.createElement('style');
      style.type = 'text/css';
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);
      charts._tooltipCssInjected = true;
    }

    const el = document.createElement('div');
    el.className = 'cj-tooltip';
    document.body.appendChild(el);
    charts._tooltipEl = el;
    return el;
  },

  _bindHoverTooltips(root = document) {
    const tooltip = charts._ensureTooltipEl();
    const candidates = Array.from(root.querySelectorAll('[title]'));

    candidates.forEach((node) => {
      const existing = node.getAttribute('data-cj-title');
      if (!existing) {
        const t = node.getAttribute('title');
        if (t != null) {
          node.setAttribute('data-cj-title', t);
          node.removeAttribute('title');
          if (!node.getAttribute('aria-label')) node.setAttribute('aria-label', t);
        }
      }

      if (node.__cjTooltipBound) return;
      node.__cjTooltipBound = true;

      const show = (evt) => {
        const text = node.getAttribute('data-cj-title');
        if (!text) return;
        tooltip.innerHTML = text;
        tooltip.style.opacity = '1';
        position(evt);
      };

      const hide = () => {
        tooltip.style.opacity = '0';
      };

      const position = (evt) => {
        const x = (evt.touches && evt.touches[0]?.clientX) || evt.clientX || 0;
        const y = (evt.touches && evt.touches[0]?.clientY) || evt.clientY || 0;

        const margin = 12;
        const rect = tooltip.getBoundingClientRect();
        let left = x;
        let top = y - margin;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (left + rect.width / 2 + margin > vw) left = vw - rect.width / 2 - margin;
        if (left - rect.width / 2 - margin < 0) left = rect.width / 2 + margin;
        if (top - rect.height - margin < 0) top = rect.height + margin * 2;

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
      };

      node.addEventListener('mouseenter', show);
      node.addEventListener('mousemove', position);
      node.addEventListener('mouseleave', hide);
      node.addEventListener('focus', (e) => show(e));
      node.addEventListener('blur', hide);
      node.addEventListener('touchstart', (e) => {
        show(e);
        clearTimeout(node.__cjTouchTimer);
        node.__cjTouchTimer = setTimeout(() => hide(), 1500);
      }, { passive: true });
      node.addEventListener('touchend', hide);
      node.addEventListener('touchcancel', hide);
    });
  },

  // ---------- Bars ----------
  _renderBars() {
    const raw = charts._getJson('chart-bars-data') || [];
    const items = raw.slice(0, 5);

    const labels = items.map(i => i.label);
    const data = items.map(i => Number(i.value));

    const ctx = charts._ensureCanvas('chart-bars', 'chart-bars-canvas');
    if (!ctx || labels.length === 0) return;

    charts._destroyIfExists('chart-bars');

    charts._instances['chart-bars'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Ticketing',
            data,
            backgroundColor: '#B5D537',
            borderColor: '#B5D537',
            borderWidth: 1,
            maxBarThickness: 48,
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 8, left: 8, right: 8 } },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { color: '#FFFFFF', boxWidth: 12, boxHeight: 12, padding: 16 }
          },
          tooltip: {
            backgroundColor: 'rgba(0,0,0,0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgba(181,213,55,0.65)',
            borderWidth: 1,
            padding: 8,
            displayColors: false,
            callbacks: {
              label: (ctx) => {
                const value = ctx.parsed.y;
                return `${ctx.dataset.label}: ${Number(value).toLocaleString()}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { color: '#424242', borderDash: [2, 2], drawBorder: false },
            ticks: { color: '#FFFFFF', maxRotation: 0, minRotation: 0, autoSkip: false }
          },
          y: {
            beginAtZero: true,
            grid: { color: '#424242', borderDash: [2, 2], drawBorder: false },
            ticks: { color: '#FFFFFF', callback: (value) => Number(value).toLocaleString() }
          }
        }
      }
    });
  },

  // ---------- Dots ----------
  _renderDots() {
    const raw = charts._getJson('chart-dots-data') || [];
    const weeks = raw.slice(0);

    const labels = weeks.map(w => `Week ${w.week}`);
    const data = weeks.map(w => Number(w.conversionRate));

    const ctx = charts._ensureCanvas('chart-dots', 'chart-dots-canvas');
    if (!ctx || labels.length === 0) return;

    charts._destroyIfExists('chart-dots');

    charts._instances['chart-dots'] = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Conversions',
            data,
            borderColor: '#B5D537',
            backgroundColor: '#B5D537',
            pointBackgroundColor: '#B5D537',
            pointBorderColor: '#B5D537',
            pointRadius: 4,
            pointHoverRadius: 5,
            borderWidth: 2,
            fill: false,
            tension: 0,
            clip: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 12, left: 12, right: 12 } },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { color: '#FFFFFF', boxWidth: 12, boxHeight: 12, padding: 16 }
          },
          tooltip: {
            backgroundColor: 'rgba(0,0,0,0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgba(181,213,55,0.65)',
            borderWidth: 1,
            padding: 8,
            displayColors: false,
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)}%`
            }
          }
        },
        scales: {
          x: {
            offset: true,
            bounds: 'ticks',
            grid: { color: '#424242', borderDash: [2, 2], drawBorder: false },
            ticks: { color: '#FFFFFF', maxRotation: 0, minRotation: 0 }
          },
          y: {
            min: 0,
            max: 100,
            ticks: { color: '#FFFFFF', padding: 6, callback: (value) => `${value}%` },
            grid: { color: '#424242', borderDash: [2, 2], drawBorder: false }
          }
        },
        elements: { point: { borderWidth: 2 } }
      }
    });
  }
};

export default charts;
